/* eslint-disable react-refresh/only-export-components */
import { onAuthStateChanged } from "firebase/auth"
import { ReactNode, useEffect } from "react"
import { auth } from "../../services/firebase"
import { useStores } from "../../models/helpers/useStores"
import { fetchUserByUid } from "../../services/firestore/user"
import { observer } from "mobx-react-lite"
import { ITrip } from "@cruisen/contract"
import { load, save } from "../../utils/storage"
import { RoomModel, TripModel, UserModel } from "@cruisen/react-shared"
import {
  getTrip,
  getUserTrips,
  subscribeNewTrips,
  subscribeTrip,
  userTripsQueryKey,
} from "../../services/firestore/trips"
import { getTripRooms, subscribeTripRooms } from "../../services/firestore/rooms"
import { getSnapshot } from "mobx-state-tree"
import { useQueryClient } from "@tanstack/react-query"
import { Analytics } from "../../utils/analytics.ts"
import { LoadingScreen } from "./LoadingScreen"
import { useLocation, useNavigate } from "react-router-dom"
import { getNavigatorStates } from "../organisms/Navigator/utils/navigatorStates.ts"
import { getAgentProfile } from "../../services/firebase-functions/agentProfile.ts"

export let roomsUnsubscribe: () => void | undefined
export let tripUnsubscribe: () => void | undefined

const getInitialTrip = (trips: ITrip[]) => {
  const lastSeenTripId = load("lastSeenTripId")
  if (lastSeenTripId) {
    const lastSeenTrip = trips.find((trip) => trip.id === lastSeenTripId)

    if (lastSeenTrip) {
      const activeTrip = TripModel.create(lastSeenTrip)
      return activeTrip
    }
  }

  const firstTrip = TripModel.create(trips[0])
  return firstTrip
}

const getTripSortingDate = (trip: ITrip) => {
  return trip.twilioConversationLastMessageDate || trip.created
}

export const AuthProvider = observer(({ children }: { children: ReactNode }) => {
  const rootStore = useStores()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const isLoggedIn = load("loggedIn")

  const isDashboard = location.pathname?.startsWith("/dashboard")

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        Analytics.configureAnalyticsCollection()
        save("loggedIn", currentUser !== null)

        if (!currentUser) {
          rootStore.setProp("loggedInUser", null)
          rootStore.setProp("agentProfile", null)
          rootStore.setProp("isAuthLoading", false)
          rootStore.setProp("isTripLoading", false)
        } else if (!rootStore.loggedInUser) {
          const userData = await fetchUserByUid(currentUser.uid)

          if (userData) {
            const loggedInUser = UserModel.create(userData)
            rootStore.setProp("loggedInUser", loggedInUser)

            const agentProfileResult = await getAgentProfile(userData.id)
            if (agentProfileResult) {
              rootStore.setProp("agentProfile", agentProfileResult.agentProfile)
            }
          } else {
            rootStore.setProp("loggedInUser", null)
            rootStore.setProp("agentProfile", null)
            rootStore.setProp("isAuthLoading", false)
            rootStore.setProp("isTripLoading", false)
          }
        }
      } catch {
        rootStore.setProp("loggedInUser", null)
        rootStore.setProp("agentProfile", null)
        rootStore.setProp("isAuthLoading", false)
        rootStore.setProp("isTripLoading", false)
        navigate("/")
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    const fetchNavigatorStates = async () => {
      const navigatorStates = await getNavigatorStates()
      rootStore.setProp("navigatorStates", navigatorStates)
      save("navigatorStates", navigatorStates)
      return navigatorStates
    }

    const fetchAgentProfile = async () => {
      if (rootStore.agentProfile) return rootStore.agentProfile

      const agentProfileResult = await getAgentProfile(rootStore.loggedInUser!.id)
      if (agentProfileResult) {
        rootStore.setProp("agentProfile", agentProfileResult.agentProfile)
        return agentProfileResult.agentProfile
      }

      return null
    }

    const fetchTripRooms = async (tripId: string) => {
      const rooms = await getTripRooms(tripId)
      rootStore.setProp("activeTrip", { ...rootStore.activeTrip, rooms })
      return rooms
    }

    const updateTrips = async (trips: ITrip[]) => {
      trips.sort((a, b) => getTripSortingDate(b) - getTripSortingDate(a))
      queryClient.setQueryData(userTripsQueryKey(rootStore.loggedInUser!.id), trips)
    }

    const fetchTripData = async () => {
      try {
        if (location.pathname === "/signup" && location.search.includes("isSignuped=true")) {
          return
        }

        const agentProfile = await fetchAgentProfile()
        if (!agentProfile) return

        const webPreviewUrls = ["/trips/"]
        const isWebPreview = webPreviewUrls.some((webPreviewUrl) =>
          location.pathname?.startsWith(webPreviewUrl),
        )

        if (isWebPreview) {
          rootStore.setProp("isAuthLoading", false)
          rootStore.setProp("isTripLoading", false)
          return
        }

        const trips = await getUserTrips(
          rootStore.loggedInUser!.id,
          rootStore?.loggedInUser?.isSuperAdmin,
        )

        if (trips.length === 0) {
          rootStore.setProp("isAuthLoading", false)
          rootStore.setProp("isTripLoading", false)
          return
        }

        subscribeNewTrips(
          rootStore.loggedInUser!.id,
          async () => {
            const trips = await getUserTrips(
              rootStore.loggedInUser!.id,
              rootStore?.loggedInUser?.isSuperAdmin,
            )

            fetchNavigatorStates()
              .then(() => {
                updateTrips(trips)
              })
              .catch(() => undefined)
          },
          rootStore?.loggedInUser?.isSuperAdmin,
        )

        updateTrips(trips)

        const activeTrip = getInitialTrip(trips)
        rootStore.setProp("activeTrip", activeTrip)

        await Promise.all([fetchNavigatorStates(), fetchTripRooms(activeTrip.id)])

        navigate(`/dashboard?tripId=${activeTrip.id}`)
      } catch (error) {
        console.error("Error fetching trip data:", error)
      } finally {
        rootStore.setProp("isAuthLoading", false)
        rootStore.setProp("isTripLoading", false)
      }
    }

    if (!rootStore.loggedInUser) return
    fetchTripData()
  }, [rootStore.loggedInUser])

  useEffect(() => {
    if (!rootStore.activeTrip?.id || !rootStore.loggedInUser?.id) return

    const refreshTripAndRoomData = async () => {
      const trips =
        queryClient.getQueryData<ITrip[]>(userTripsQueryKey(rootStore.loggedInUser!.id)) || []

      if (trips.length <= 1) return

      const tripData = await getTrip(rootStore.activeTrip.id)
      const rooms = await getTripRooms(rootStore.activeTrip.id)

      rootStore.setProp(
        "activeTrip",
        TripModel.create({ ...tripData, rooms: rooms.map((room) => RoomModel.create(room)) }),
      )

      queryClient.setQueryData(
        userTripsQueryKey(rootStore.loggedInUser!.id),
        trips.map((trip) =>
          trip.id === tripData.id
            ? {
                ...tripData,
                rooms,
              }
            : trip,
        ),
      )
    }

    refreshTripAndRoomData()

    tripUnsubscribe?.()
    roomsUnsubscribe?.()

    tripUnsubscribe = subscribeTrip(rootStore.activeTrip.id, (tripData) => {
      const currentTripSnapshot = getSnapshot(rootStore.activeTrip)
      if (currentTripSnapshot.collaborators.length !== tripData.collaborators.length) {
        queryClient.invalidateQueries({ queryKey: ["allProfiles", rootStore.activeTrip.id] })
      }
      const updatedTrip: ITrip = {
        ...currentTripSnapshot,
        ...tripData,
      }

      rootStore.setProp("activeTrip", TripModel.create(updatedTrip))
    })

    roomsUnsubscribe = subscribeTripRooms(rootStore.activeTrip.id, (updatedRooms) => {
      queryClient.invalidateQueries({ queryKey: ["allProfiles", rootStore.activeTrip.id] })
      const currentTripSnapshot = getSnapshot(rootStore.activeTrip)
      const updatedTrip = {
        ...currentTripSnapshot,
        rooms: updatedRooms,
      }

      rootStore.setProp("activeTrip", TripModel.create(updatedTrip))
    })

    return () => {
      tripUnsubscribe?.()
      roomsUnsubscribe?.()
    }
  }, [rootStore.activeTrip?.id, rootStore.loggedInUser?.id])

  if (isLoggedIn && rootStore.isAuthLoading && !isDashboard) {
    return <LoadingScreen />
  }

  return children
})
